import { useCallback } from 'react'
import { useReactFlow } from '@xyflow/react'
import { AIService } from '../../../services/ai/aiService'
import { useLazyTrigger, createCategoryFilter, getLabelContent } from './useLazyTrigger'
import type { IdeationPhase } from '@bandos/shared'
import type { Node } from '@xyflow/react'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'

// Debounce delay for phase assignment (5 seconds after user stops typing)
const PHASE_ASSIGNMENT_DEBOUNCE_MS = 5000

// Minimum content length to trigger phase assignment
const MIN_CONTENT_LENGTH = 10

interface UsePhaseAssignmentProps {
  mindmapId: string | undefined
  enabled?: boolean
}

/**
 * Hook that automatically assigns phases to idea nodes when their content changes.
 * Uses the modular useLazyTrigger hook for debounced, change-detected triggering.
 */
export function usePhaseAssignment({ mindmapId, enabled = true }: UsePhaseAssignmentProps) {
  const { setNodes } = useReactFlow()

  // Handler for successful phase assignment
  const handlePhaseAssigned = useCallback(
    (node: Node<ReactFlowNodeData>, result: { phase?: number }) => {
      if (result.phase) {
        const newPhase = result.phase as IdeationPhase
        const currentPhase = node.data.phase

        // Only update if phase actually changed
        if (newPhase !== currentPhase) {
          setNodes((currentNodes) =>
            currentNodes.map((n) => {
              if (n.id === node.id && n.data.category === 'idea') {
                return {
                  ...n,
                  data: {
                    ...n.data,
                    phase: newPhase,
                  },
                }
              }
              return n
            })
          )
        }
      }
    },
    [setNodes]
  )

  // Use the modular lazy trigger hook
  useLazyTrigger<{ phase?: number }>(
    {
      id: 'phase-assignment',
      debounceMs: PHASE_ASSIGNMENT_DEBOUNCE_MS,
      minContentLength: MIN_CONTENT_LENGTH,
      nodeFilter: createCategoryFilter(['idea']),
      getContent: getLabelContent,
      onTrigger: async (node) => {
        if (!mindmapId) {
          throw new Error('MindmapId not available')
        }
        const response = await AIService.assignPhase(mindmapId, node.id)
        if (response.error) {
          throw new Error(response.error.message)
        }
        return { phase: response.data?.phase }
      },
      onSuccess: handlePhaseAssigned,
      onError: (_node, error) => {
        console.warn('[usePhaseAssignment] Failed to assign phase:', error)
      },
    },
    enabled && !!mindmapId
  )
}
